import { Component, Input, OnInit, inject } from '@angular/core';
import { Capture } from '../../../core/models/capture.model';
import { AuthenticationService } from '../../../core/services/authentication.service';
import { CaptureStatus } from '../../../core/enums/capture-status.enum';
import { CaptureService } from 'app/core/services/capture.service';
import { UserRoleName } from 'app/core/enums/user-role-name.enum';
import { NgClass, DatePipe } from '@angular/common';
import { RouterLinkActive, RouterLink } from '@angular/router';
import { UserPresenceComponent } from '../../../shared/components/user-presence/user-presence.component';
import { CaptureSnapshotComponent } from '../capture-snapshot/capture-snapshot.component';
import { CaptureReprocessComponent } from '../capture-reprocess/capture-reprocess.component';
import { CaptureUnverifyComponent } from '../capture-unverify/capture-unverify.component';
import { CaptureNextComponent } from '../capture-next/capture-next.component';
import { ClientNameComponent } from '../../../shared/components/client-name/client-name.component';
import { PatientNameComponent } from '../../../shared/components/patient-name/patient-name.component';
import { CandidatePatientNameComponent } from '../candidate-patient-name/candidate-patient-name.component';
import { ExpirationDateComponent } from '../../../shared/components/expiration-date/expiration-date.component';
import { HighValueIndicatorComponent } from '../../../shared/components/high-value-indicator/high-value-indicator.component';
import { CandidateDrugComponent } from '../candidate-drug/candidate-drug.component';
import { PrescriberNameComponent } from '../../../shared/components/prescriber-name/prescriber-name.component';
import { PrescriberOfficesComponent } from '../../../shared/components/prescriber-offices/prescriber-offices.component';
import { CaptureStatusPipe } from 'app/shared/pipes/capture-status.pipe';
import { CaptureStatusReasonPipe } from '../../../shared/pipes/capture-status-reason.pipe';
import { JoinListPipe } from '../../../shared/pipes/join-list.pipe';
import { CapitalizePipe } from '../../../shared/pipes/capitalize.pipe';

@Component({
    selector: 'app-capture-header',
    templateUrl: './capture-header.component.html',
    styleUrls: ['./capture-header.component.scss'],
    imports: [NgClass,
              RouterLinkActive,
              RouterLink,
              UserPresenceComponent,
              CaptureSnapshotComponent,
              CaptureReprocessComponent,
              CaptureUnverifyComponent,
              CaptureNextComponent,
              ClientNameComponent,
              PatientNameComponent,
              CandidatePatientNameComponent,
              ExpirationDateComponent,
              HighValueIndicatorComponent,
              CandidateDrugComponent,
              PrescriberNameComponent,
              PrescriberOfficesComponent,
              DatePipe,
              CaptureStatusPipe,
              CaptureStatusReasonPipe,
              JoinListPipe,
              CapitalizePipe]
})
export class CaptureHeaderComponent implements OnInit {
  private authService = inject(AuthenticationService);
  private captureService = inject(CaptureService);

  private _capture: Capture;
  private captureChangedSubscription = null;

  canSnapshot = false;
  canReprocess = false;
  canViewHistory = false;
  canUnverify = false;
  canGoToNext = false;
  canViewFax = false;
  canViewPatient = false;
  canViewClient = false;
  showEstimatedValue = false;
  showClientReviewTask = false;
  isHidden = false;

  @Input()
  set capture(capture: Capture) {
    this._capture = capture;
    this.isHidden = capture.status === CaptureStatus.needsPatientMatchApproval && this.authService.isCaptureAdminUser;
    this.showClientReviewTask = this.authService.isCaptureAdminUser;
    this.canUnverify = this.authService.isCaptureAdminUser &&
                       this.authService.hasPermissionTo(UserRoleName.editVerifiedCaptures) &&
                       this.capture.unverifiable;
    this.canViewClient = this.authService.isCaptureAdminUser;
  }

  get capture(): Capture {
    return this._capture;
  }

  ngOnInit() {
    this.canSnapshot = this.authService.isCaptureAdminUser;
    this.canViewHistory = this.authService.isCaptureAdminUser;
    this.canViewFax = this.authService.isCaptureAdminUser;
    this.showEstimatedValue = this.authService.isCaptureAdminUser;
    this.canReprocess = this.authService.isCaptureAdminUser;
    this.canGoToNext = this.authService.isCaptureAdminUser;
    this.canViewPatient = this.authService.isCaptureAdminUser;

    this.captureChangedSubscription = this.captureService.captureChanged.subscribe(({ capture }) => {
      if (capture.id === this.capture.id) {
        this.capture = capture;
      }
    });
  }

  public get showHealthGorillaTag(): boolean {
    const clientEnabled = this.capture.client.healthGorillaSettings?.enabled;
    const userAllowed = this.authService.hasPermissionTo(UserRoleName.accessHieData);
    const patientHasData = !!this.capture.patient.healthGorillaIdentifier;

    return clientEnabled && userAllowed && patientHasData
  }

  onShowClick() {
    this.isHidden = false;
  }
}
